/**
 * Projection facade — historical entry point.
 *
 * Pre-existing modules import { worldToScreen, screenToWorld, ... } from "./Iso".
 * Those calls now delegate to the active Projection (default: IsoProjection),
 * so callers don't need to know whether the current zone is iso or top-down.
 *
 * For per-zone tile dimensions in projection-aware drawing code, prefer
 * `currentProjection().tileWidth/tileHeight` over the iso-specific TILE constants.
 * The `TILE` export remains for legacy iso drawing only (diamond textures, side
 * faces, dock decorations) — those code paths run only when projection is iso.
 */

import { IsoProjection } from "./projection/IsoProjection";
import type { Projection } from "./projection/Projection";

export type { ScreenPos, WorldPos } from "./projection/Projection";

/** Iso-specific constants. Only used inside iso drawing code paths. */
export const TILE = {
  width: 64,
  height: 32,
  halfW: 32,
  halfH: 16,
} as const;

export const MAP_SIZE = 16;

let _current: Projection = new IsoProjection();

export function setCurrentProjection(p: Projection): void {
  _current = p;
}

export function currentProjection(): Projection {
  return _current;
}

export function worldToScreen(x: number, y: number) {
  return _current.worldToScreen(x, y);
}

export function tileFoot(x: number, y: number) {
  return _current.tileFoot(x, y);
}

export function screenToWorld(sx: number, sy: number) {
  return _current.screenToWorld(sx, sy);
}

export function defaultIsoOrigin(viewportW: number, viewportH: number, gridSize: number = MAP_SIZE) {
  return _current.defaultOrigin(viewportW, viewportH, gridSize);
}

export function rotateInputToWorld(rx: number, ry: number) {
  return _current.rotateInputToWorld(rx, ry);
}

export function worldDirToFrame(dir: number): number {
  return _current.worldDirToFrame(dir);
}
